import React, { useContext, useEffect, useState } from "react";
import Button from "../../Elements/Button";
import CommonModal from "../CommonModal";
import Input from "../../Elements/Input";
import { networkService } from "../../config/axios";
import apiGETRequest from "../../../utils/apiRequest";
import UserInfoContext from "../../../utils/userInfoContext";
import "./myProfileModal.css";

const FIELDS = [
    { name: "company_name", label: "Company Name", individualLabel: "Business Name", placeholder: "Your company or business name" },
    { name: "company_website", label: "Company Website", individualLabel: "Your Website", placeholder: "https://yourcompany.com" },
    {
        name: "privacy_link",
        label: "Privacy Notice URL",
        individualLabel: "Your Privacy Notice URL",
        placeholder: "https://yourcompany.com/privacy",
        hint: "Must be on the same domain as your website. Most websites link it in the footer as \"Privacy\" or \"Privacy Policy\".",
    },
];

const PrivacyNoticeRequiredModal = () => {
    const { userInfoData, refetchUserProfile } = useContext(UserInfoContext);
    const [open, setOpen] = useState(false);
    const [locked, setLocked] = useState({});
    const [individual, setIndividual] = useState(false);
    const [values, setValues] = useState({});
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const show = async () => {
            const profile = userInfoData?.id ? await apiGETRequest(`profiles/${userInfoData.id}.json`) : null;
            setIndividual(!!profile?.individual);
            setLocked({ company_name: !!profile?.company_name_locked, company_website: !!profile?.company_website_locked });
            setValues({
                company_name: profile?.company_name || "",
                company_website: profile?.company_website || "",
                privacy_link: profile?.privacy_link || "",
            });
            setErrors({});
            setOpen(true);
        };
        window.addEventListener("app:privacy-link-required", show);
        return () => window.removeEventListener("app:privacy-link-required", show);
    }, [userInfoData?.id]);

    const fields = FIELDS.filter((field) => !locked[field.name]);

    const close = () => {
        setOpen(false);
        window.dispatchEvent(new CustomEvent("app:privacy-link-cancelled"));
    };

    const save = async () => {
        const missing = fields.reduce((acc, field) => {
            if (!values[field.name]?.trim()) acc[field.name] = "Required";
            return acc;
        }, {});
        if (Object.keys(missing).length) return setErrors(missing);

        setSaving(true);
        try {
            const payload = fields.reduce((acc, field) => ({ ...acc, [field.name]: values[field.name].trim() }), {});
            await networkService(`profiles/${userInfoData.id}.json`, "PATCH", { profile: payload, user_id: userInfoData.id });
            setOpen(false);
            refetchUserProfile?.();
            window.dispatchEvent(new CustomEvent("app:privacy-link-saved"));
        } catch (error) {
            const serverErrors = error?.response?.data?.errors || {};
            const fieldErrors = fields.reduce((acc, field) => {
                if (serverErrors[field.name]) acc[field.name] = serverErrors[field.name].join(", ");
                return acc;
            }, {});
            if (!Object.keys(fieldErrors).length) {
                fieldErrors.general = "Could not save. Please complete your profile under My Profile and try again.";
            }
            setErrors(fieldErrors);
        } finally {
            setSaving(false);
        }
    };

    return (
        <CommonModal
            title="Add your Privacy Notice to share"
            modalStatus={open}
            changeActiveStatus={(status) => !status && close()}
            classList="header-profile-modal-wrapper privacy-required-modal"
            backdropClassName="privacy-required-backdrop"
            centered
            footer={
                <>
                    <Button classList="success" addonClass="d-inline-flex ml-4" onClick={close} text="Cancel" />
                    <Button classList="success" addonClass="d-inline-flex ml-4 common-modal-yes-button" onClick={save} disabled={saving} text={saving ? "Saving..." : "Save and continue"} />
                </>
            }
        >
            <p style={{ fontSize: "14px", marginBottom: "16px" }}>
                People who open your LaserLINX are told how their data is handled through <strong>your</strong> Privacy Notice, not Kinetizine's, because you are responsible for the data your links collect.
            </p>
            {fields.map((field) => (
                <div key={field.name} className="mb-3">
                    <Input
                        name={field.name}
                        label={individual ? field.individualLabel : field.label}
                        required
                        placeholder={field.placeholder}
                        value={values[field.name] || ""}
                        onChange={(e) => {
                            setValues({ ...values, [field.name]: e.target.value });
                            setErrors({ ...errors, [field.name]: undefined });
                        }}
                        errorMessageStatus={!!errors[field.name]}
                        errorMessage={errors[field.name] && <span className="text-danger">{errors[field.name]}</span>}
                    >
                        {field.hint && <p className="geo-provider-hint" style={{ marginTop: "6px" }}>{field.hint}</p>}
                    </Input>
                </div>
            ))}
            {errors.general && <p className="text-danger" style={{ fontSize: "13px" }}>{errors.general}</p>}
        </CommonModal>
    );
};

export default PrivacyNoticeRequiredModal;
